import { useState } from 'react';
import { accommodations, type Accommodation } from '../../../mocks/accommodations';

interface AccommodationGridProps {
  selectedCountry: string;
}

function AccommodationCard({ accommodation }: { accommodation: Accommodation }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer">
      <div className="relative h-64 w-full overflow-hidden bg-gray-100">
        {!imgError ? (
          <img
            src={accommodation.image}
            alt={accommodation.name}
            loading="lazy"
            onError={() => setImgError(true)}
            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#f5f0e8] to-[#e8dcc8]">
            <div className="w-16 h-16 flex items-center justify-center mb-3">
              <i className="ri-hotel-line text-[#C9A961] text-5xl"></i>
            </div>
            <p className="text-sm text-gray-500 font-medium">{accommodation.name}</p>
          </div>
        )}
        <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full shadow-lg">
          <span className="text-sm font-semibold text-[#1A1F2E]">{accommodation.type}</span>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-xl font-bold text-[#1A1F2E] mb-1">{accommodation.name}</h3>
            <p className="text-sm text-gray-600 flex items-center gap-1">
              <i className="ri-map-pin-line text-[#C9A961]"></i>
              {accommodation.location}
            </p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 mb-1">
              <i className="ri-star-fill text-[#C9A961] text-sm"></i>
              <span className="font-bold text-[#1A1F2E]">{accommodation.rating}</span>
            </div>
            <p className="text-xs text-gray-500">{accommodation.reviews} reviews</p>
          </div>
        </div>

        <div className="mb-4 pb-4 border-b border-gray-100">
          <p className="text-sm text-gray-600 flex items-center gap-2">
            <i className="ri-map-2-line text-[#C9A961]"></i>
            {accommodation.distance}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {accommodation.features.map((feature, index) => (
            <span key={index} className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
              {feature}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div>
            <p className="text-sm text-gray-600">From</p>
            <p className="text-2xl font-bold text-[#1A1F2E]">
              £{accommodation.price}
              <span className="text-sm font-normal text-gray-600">/night</span>
            </p>
          </div>
          <button
            onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
            className="px-6 py-2 bg-[#C9A961] text-white rounded-lg font-semibold hover:bg-[#B89951] transition-all whitespace-nowrap cursor-pointer"
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AccommodationGrid({ selectedCountry }: AccommodationGridProps) {
  const getAccommodations = (): Accommodation[] => {
    switch (selectedCountry) {
      case 'saudi-arabia': return accommodations.saudiArabia;
      case 'turkey': return accommodations.turkey;
      case 'uae': return accommodations.uae;
      case 'morocco': return accommodations.morocco;
      case 'egypt': return accommodations.egypt;
      case 'jordan': return accommodations.jordan;
      case 'malaysia': return accommodations.malaysia;
      case 'indonesia': return accommodations.indonesia;
      case 'maldives': return accommodations.maldives;
      case 'bosnia': return accommodations.bosnia;
      default: return accommodations.saudiArabia;
    }
  };

  const currentAccommodations = getAccommodations();

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2
            className="text-4xl md:text-5xl font-bold text-[#1A1F2E] mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Hand-Selected Accommodations
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Every property is personally verified for quality, comfort, and cultural alignment
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {currentAccommodations.map((accommodation) => (
            <AccommodationCard key={accommodation.id} accommodation={accommodation} />
          ))}
        </div>
      </div>
    </section>
  );
}
